import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, ArrowUpRight, CalendarClock } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { api, SERVICES, BUSINESS } from "@/lib/site";

const TIMES = ["7:00 AM", "9:00 AM", "11:00 AM", "1:00 PM", "3:00 PM", "5:00 PM"];

export default function Booking() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        preferred_date: "",
        preferred_time: "",
        notes: "",
    });
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const total = useMemo(
        () => selected.reduce((sum, id) => sum + (SERVICES.find((s) => s.id === id)?.price || 0), 0),
        [selected],
    );

    const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const toggle = (id) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const submit = async (e) => {
        e.preventDefault();
        if (selected.length === 0) return toast.error("Please select at least one service.");
        if (!form.name || !form.phone || !form.email || !form.address || !form.preferred_date || !form.preferred_time)
            return toast.error("Please fill in all required fields.");
        setLoading(true);
        try {
            await api.post("/bookings", { ...form, services: selected });
            setDone(true);
            toast.success("Booking sent! Ben will reach out to confirm.");
        } catch (err) {
            toast.error("Something went wrong. Please call us instead.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <main data-testid="booking-success" className="min-h-[100svh] bg-forest text-sand grain flex items-center">
                <div className="relative z-10 mx-auto max-w-2xl px-5 text-center py-32">
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 14 }}
                        className="inline-flex h-20 w-20 rounded-full bg-sky text-forest items-center justify-center mb-8"
                    >
                        <Check size={38} />
                    </motion.span>
                    <h1 className="font-heading font-extrabold tracking-tight text-4xl md:text-6xl">You're on the list!</h1>
                    <p className="mt-5 text-sand/80 text-lg">
                        Thanks {form.name.split(" ")[0]} — your request for {form.preferred_date} is in. {BUSINESS.owner}{" "}
                        will call {form.phone} to confirm your ${total} clean.
                    </p>
                    <a
                        href="/"
                        className="mt-10 inline-flex items-center gap-2 rounded-full bg-sand text-forest px-7 py-4 font-semibold hover:bg-sky transition-colors"
                    >
                        Back to home <ArrowUpRight size={18} />
                    </a>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="booking-page" className="bg-sand pt-[74px]">
            <section className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24">
                <Reveal>
                    <p className="text-sm uppercase tracking-[0.25em] text-forest/50 mb-4 flex items-center gap-2">
                        <CalendarClock size={16} /> Book an appointment
                    </p>
                    <h1 className="font-heading font-extrabold tracking-tight text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
                        Let's get your curb <span className="text-sky">spotless.</span>
                    </h1>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* FORM */}
                    <form onSubmit={submit} id="booking-form" className="lg:col-span-8 space-y-10" data-testid="booking-form">
                        {/* Services */}
                        <Reveal>
                            <div>
                                <h2 className="font-heading font-bold text-2xl text-forest mb-5">1. Choose your services</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {SERVICES.map((s) => {
                                        const active = selected.includes(s.id);
                                        return (
                                            <button
                                                type="button"
                                                key={s.id}
                                                data-testid={`service-option-${s.id}`}
                                                onClick={() => toggle(s.id)}
                                                className={`text-left rounded-2xl border p-5 transition-[background,border,transform] duration-200 hover:-translate-y-0.5 ${
                                                    active
                                                        ? "bg-forest text-sand border-forest"
                                                        : "bg-white text-forest border-black/10 hover:border-forest/40"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <p className="font-heading font-bold text-lg">{s.title}</p>
                                                        <p className={`text-xs uppercase tracking-widest ${active ? "text-sky" : "text-forest/40"}`}>
                                                            {s.unit}
                                                        </p>
                                                    </div>
                                                    <span
                                                        className={`h-6 w-6 shrink-0 rounded-md border flex items-center justify-center ${
                                                            active ? "bg-sky border-sky text-forest" : "border-forest/30"
                                                        }`}
                                                    >
                                                        {active && <Check size={15} />}
                                                    </span>
                                                </div>
                                                <p className="mt-3 font-heading font-extrabold text-2xl">${s.price}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </Reveal>

                        {/* Details */}
                        <Reveal delay={0.05}>
                            <div>
                                <h2 className="font-heading font-bold text-2xl text-forest mb-5">2. Your details</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Full name *" testid="input-name" value={form.name} onChange={setField("name")} placeholder="Jane Doe" />
                                    <Field label="Phone *" testid="input-phone" value={form.phone} onChange={setField("phone")} placeholder="916 555 1234" />
                                    <Field label="Email *" testid="input-email" type="email" value={form.email} onChange={setField("email")} placeholder="jane@email.com" />
                                    <Field label="Service address *" testid="input-address" value={form.address} onChange={setField("address")} placeholder="123 Oak St, Roseville" />
                                </div>
                            </div>
                        </Reveal>

                        {/* Schedule */}
                        <Reveal delay={0.1}>
                            <div>
                                <h2 className="font-heading font-bold text-2xl text-forest mb-5">3. Pick a time</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Preferred date *" testid="input-date" type="date" value={form.preferred_date} onChange={setField("preferred_date")} />
                                    <div>
                                        <label className="block text-sm font-medium text-forest/70 mb-2">Preferred time *</label>
                                        <div className="flex flex-wrap gap-2">
                                            {TIMES.map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    data-testid={`time-${t.replace(/[: ]/g, "")}`}
                                                    onClick={() => setForm((f) => ({ ...f, preferred_time: t }))}
                                                    className={`rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${
                                                        form.preferred_time === t
                                                            ? "bg-forest text-sand border-forest"
                                                            : "bg-white text-forest border-black/10 hover:border-forest/40"
                                                    }`}
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-forest/70 mb-2">Notes (optional)</label>
                                    <textarea
                                        data-testid="input-notes"
                                        value={form.notes}
                                        onChange={setField("notes")}
                                        rows={3}
                                        placeholder="Gate code, bin location, anything we should know…"
                                        className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-forest transition-colors resize-none"
                                    />
                                </div>
                            </div>
                        </Reveal>
                    </form>

                    {/* SUMMARY */}
                    <Reveal delay={0.12} className="lg:col-span-4">
                        <div className="sticky top-24 rounded-3xl bg-forest text-sand p-8 grain overflow-hidden">
                            <div className="relative z-10">
                                <h3 className="font-heading font-bold text-xl mb-6">Your estimate</h3>
                                {selected.length === 0 ? (
                                    <p className="text-sand/60 text-sm">Select services to see your total.</p>
                                ) : (
                                    <ul className="space-y-3">
                                        {selected.map((id) => {
                                            const s = SERVICES.find((x) => x.id === id);
                                            return (
                                                <li key={id} className="flex justify-between text-sm">
                                                    <span className="text-sand/80">{s.title}</span>
                                                    <span className="font-semibold">${s.price}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                                <div className="mt-6 pt-6 border-t border-sand/15 flex items-end justify-between">
                                    <span className="text-sand/70 text-sm uppercase tracking-widest">Total</span>
                                    <span data-testid="booking-total" className="font-heading font-extrabold text-4xl">
                                        ${total}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={loading}
                                    data-testid="booking-submit"
                                    className="mt-8 w-full rounded-full bg-sand text-forest py-4 font-semibold hover:bg-sky transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-70"
                                >
                                    {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowUpRight size={18} />}
                                    {loading ? "Sending…" : "Confirm booking"}
                                </button>
                                <p className="mt-4 text-center text-xs text-sand/50">
                                    Or call {BUSINESS.owner} at {BUSINESS.phone}
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}

function Field({ label, testid, type = "text", value, onChange, placeholder }) {
    return (
        <div>
            <label className="block text-sm font-medium text-forest/70 mb-2">{label}</label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 outline-none focus:border-forest transition-colors"
            />
        </div>
    );
}
